// components/common/DateQueryPicker.tsx
import { useCallback, useEffect, useState } from 'react';

import { parse } from 'date-fns';
import { CalendarProps } from 'primereact/calendar';
import { useSearchParams } from 'react-router-dom';

import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { formatURLDate } from '@/utils';

type Props = {
  paramKey?: string;
  [key: string]: any;
};

export default function DateQueryPicker({ paramKey = 'd', ...rest }: Props) {
  const [params, setParams] = useSearchParams();

  const [value, setValue] = useState<Date | null>(() => {
    const raw = params.get(paramKey);
    return raw ? parse(raw, 'MM-dd-yyyy', new Date()) : null;
  });

  const writeParam = useCallback(
    (d: Date | null) => {
      const next = new URLSearchParams(params);

      if (d) {
        const iso = d.toISOString();
        next.set(paramKey, formatURLDate(iso));
      } else {
        next.delete(paramKey);
      }
      setParams(next);
    },
    [paramKey, params, setParams],
  );

  const handleChange = (
    e: Parameters<NonNullable<CalendarProps['onChange']>>[0],
  ) => {
    const d = e.value as Date | null;
    setValue(d);
    writeParam(d);
  };

  useEffect(() => {
    const raw = params.get(paramKey);
    setValue(raw ? parse(raw, 'MM-dd-yyyy', new Date()) : null);
  }, [params, paramKey]);

  return (
    <Calendar
      value={value}
      onChange={(
        e: CalendarProps['onChange'] extends (event: infer E) => void
          ? E
          : never,
      ) => handleChange(e)}
      showIcon
      placeholder="Date"
      dateFormat="mm-dd-yy"
      {...rest}
    />
  );
}
